"use client"

/**
 * Keeps `<html class="theme-prism">` on products home and every Prism suite app.
 *
 * Runs beside ProductProvider so tenant custom products that mirror built-in
 * slugs (`Compliance` → `/compliance`) cannot leave the shell on green
 * `theme-custom` chrome.
 */

import * as React from "react"
import { useLocation } from "react-router"
import { useAppStore } from "@exxatdesignux/product-framework/app-store"

import {
  applyPrismDocumentTheme,
  documentThemeNeedsPrismResync,
  usesPrismDocumentTheme,
} from "@/lib/prism-document-theme"
import { scrubStalePrismSuiteAccentOverrides } from "@/lib/prism-suite-brand-sync"

export function PrismDocumentThemeSync() {
  const { pathname } = useLocation()
  const product = useAppStore(s => s.product)

  React.useLayoutEffect(() => {
    const shouldUsePrism = () =>
      usesPrismDocumentTheme(
        window.location.pathname,
        useAppStore.getState().product,
      )

    const apply = () => {
      if (!shouldUsePrism()) return
      scrubStalePrismSuiteAccentOverrides()
      applyPrismDocumentTheme()
    }

    apply()
    const raf = requestAnimationFrame(apply)

    const observer = new MutationObserver(() => {
      if (!shouldUsePrism()) return
      if (documentThemeNeedsPrismResync()) {
        requestAnimationFrame(apply)
      }
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    })

    const unsub = useAppStore.subscribe((state, prev) => {
      if (
        state.product !== prev.product ||
        state.productBrandColors !== prev.productBrandColors ||
        state.customProducts !== prev.customProducts
      ) {
        apply()
      }
    })

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
      unsub()
    }
  }, [pathname, product])

  return null
}
